import React, { useState, useEffect } from "react";
import { Amplitude, LogOnMount, useAmplitude } from "react-amplitude-hooks";
import styled from "@emotion/styled";
import { isIE, isChrome, osVersion } from "react-device-detect";

import {
  planTypeName,
  stringToDate,
  numberWithCommas,
  numberToTell,
  setScoping
} from "components/Travel/function";

import {
  DashedBox,
  ButtonClose,
  ButtonText,
  Notice
} from "style/components/pc";
import DefinitionLine from "components/Common/PC/DefinitionLine";
import iconAirplane from "images/pc/icon-airplane.svg";
import { countryName } from "components/Travel/country";
import PlanPopup from "components/Travel/PC/PlanPopup";
import PopupPortal from "components/Common/Shared/PopupPortal";

interface Props {
  data: any;
  onClose: any;
}

const SubscriptionDetail: React.FunctionComponent<Props> = ({
  data,
  onClose
}) => {
  const [isShowPopup, togglePopup] = useState(false);
  const [personType, setPlanContent] = useState({
    contents: [{}],
    name: "",
    code: ""
  });
  const { logEvent } = useAmplitude();
  useEffect(() => {
    document.body.classList.add("scrollHidden");

    return () => {
      document.body.classList.remove("scrollHidden");
    };
  });

  const partnerList = () => {
    const relatorInfo = data.travelInfo.companionInfoList;

    return relatorInfo.filter(
      (relator: any, relatorIndex: number) => relatorIndex !== 0
    );
  };

  const renderPrice = () => {
    return (
      <>
        <span>{numberWithCommas(data.totalAmount)}원</span>
        <TotalPriceInfo> 결제완료</TotalPriceInfo>
      </>
    );
  };

  const renderPeriod = () => {
    return (
      <span>
        {stringToDate(data.policyStartDate)} ({data.policyStartDateTime}시)
        {" ~ "}
        {stringToDate(data.policyEndDate)} ({data.policyEndDateTime}시)
      </span>
    );
  };

  const renderPerPersonPrice = () => {
    const relatorInfo = data.travelInfo.companionInfoList;
    return relatorInfo.map((item: any, index: number) => {
      return (
        <PersonPriceInfoWrap key={`person${index}`}>
          <PersonPriceInfo>
            {item.name}
            {` _ `}
            {planTypeName(item.planCode)} {numberWithCommas(item.amount)}원
          </PersonPriceInfo>
          <ButtonText
            type="button"
            onClick={() => {
              logEvent("history_info_button", {
                plan_type: planTypeName(item.planCode)
              });
              onClickPlanButton(
                setScoping(item.birth, item.planCode),
                item.name,
                item.planCode
              );
            }}
          >
            보장내용
          </ButtonText>
        </PersonPriceInfoWrap>
      );
    });
  };

  const onClickPlanButton = (contents: any[], name: string, code: string) => {
    setPlanContent({
      contents,
      name,
      code
    });
    togglePopup(!isShowPopup);
  };

  const renderPartnerName = () => {
    const list = partnerList();
    return list.map((person: any, index: number) => {
      if (list.length - 1 === index) {
        return <span key={`name${index}`}>{person.name}</span>;
      }
      return <span key={`name${index}`}>{person.name}, </span>;
    });
  };

  return (
    <Amplitude>
      <LogOnMount eventType="history_detail" />
      <TicketWrap>
        <Ticket
          className={`${isIE ? "ie" : ""} ${
            osVersion === "Windows" && isChrome ? "windowChrome" : ""
          }`}
        >
          <div className="infoWrap">
            <DashedBox className="first">
              <DestinationSmallBox>
                대한민국
                <span className="iconAirplane">
                  <img src={iconAirplane} alt="" />
                </span>
                <span className="destination">
                  {countryName(data.travelInfo.country)}
                </span>
              </DestinationSmallBox>
            </DashedBox>
            <DashedBox>
              <DefinitionLine title="보험명" content="Chubb 해외여행보험" />
              <DefinitionLine title="보험기간" content={renderPeriod()} />
              <DefinitionLine title="가입일시" content={data.insuranceDate} />
              <DefinitionLine
                title="가입인원"
                content={data.travelInfo.companionInfoList.length + "명"}
              />
              <DefinitionLine title="보험료" content={renderPrice()} />
              <DefinitionLine
                title=""
                content={renderPerPersonPrice()}
                className="partnerList"
              />
            </DashedBox>
            <DashedBox>
              <DefinitionLine
                title="대표 피보험자"
                content={data.insuredInfo.name}
              />
              <DefinitionLine
                title="휴대전화번호"
                content={numberToTell(data.insuredInfo.telMobile)}
              />
              <DefinitionLine title="이메일" content={data.insuredInfo.email} />
              {data.travelInfo.companionInfoList.length > 1 && (
                <DefinitionLine
                  title="추가 동반자"
                  content={renderPartnerName()}
                />
              )}
            </DashedBox>
            <DashedBox>
              <Notice className="certificateEng">
                영문 가입 증명서가 필요하신 경우 이메일로 성함, 휴대전화번호를{" "}
                입력하여 내주세요.{" "}
                <a href="mailto: help@bomappbiz.co.kr" className="underline">
                  help@bomappbiz.co.kr
                </a>
                <br />
                (영업일 기준 1~2일 내 발급)
              </Notice>
            </DashedBox>
          </div>
          <ButtonClose
            type="button"
            className="ticketClose"
            onClick={() => {
              logEvent("history_list_button");
              onClose();
            }}
            aria-label="팝업 닫기"
          />
        </Ticket>
      </TicketWrap>
      {isShowPopup ? (
        <PopupPortal>
          <PlanPopup
            togglePopup={() => togglePopup(!isShowPopup)}
            contents={personType.contents}
            name={personType.name}
            planType={personType.code}
          />
        </PopupPortal>
      ) : null}
    </Amplitude>
  );
};

const TicketWrap = styled.div`
  z-index: 100;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.6);
`;

const Ticket = styled.div`
  position: relative;
  width: 420px;
  border-radius: ${props => props.theme.radius};
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.2);

  .infoWrap {
    overflow-y: auto;
    max-height: 559px;
    padding: 29px 46px 25px;
    border-radius: ${props => props.theme.radius};
    background-color: ${props => props.theme.colors.white};
  }

  .ticketClose {
    position: absolute;
    top: 0;
    right: -51px;
  }

  .certificateEng {
    position: relative;
    margin-top: 5px;
    padding-left: 15px;

    &::before {
      content: "ⓘ";
      position: absolute;
      top: 0;
      left: 0;
    }
  }
`;

const DestinationSmallBox = styled.p`
  font-size: ${props => props.theme.text.sizeFontL};
  font-weight: ${props => props.theme.weight.extraBold};

  .iconAirplane {
    display: inline-block;
    margin-left: 16px;
    margin-right: 16px;
    width: 22px;
    height: 16px;
    vertical-align: middle;

    > img {
      width: 100%;
    }
  }
`;

const TotalPriceInfo = styled.span`
  color: ${props => props.theme.colors.primary};
`;

const PersonPriceInfoWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 2px;
`;

const PersonPriceInfo = styled.span`
  display: block;
  color: ${props => props.theme.text.colorFont};
  font-size: ${props => props.theme.text.sizeFont};
`;

export default SubscriptionDetail;
